import { Form, Select } from "antd";
import { useSearchParams, useSubmit } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { removeEmptyFields } from "~/utils";
import * as APITypes from "~/API";
import { ReservationStatusTag } from "~/components/Tags/ReservationStatusTag";
import { ReservationListFiltersType } from "~/types";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import {
  ResponsiveFilterChip,
  ResponsiveFilterGrid,
  ResponsiveFilterPanel,
} from "~/components/Filters";
import { useMemo } from "react";

export const ReservationListFilters = ({
  filters,
}: {
  filters: ReservationListFiltersType;
}) => {
  const { t } = useAppTranslation();
  const [formInstance] = Form.useForm();
  const [searchParams] = useSearchParams();
  const submit = useSubmit();
  const { isTableLoading: isLoading } = useRouteActivity();

  const initialValues = useMemo(
    () => ({
      status:
        filters.status ??
        (searchParams.get("status") as
          | APITypes.AmenityReservationStatus
          | undefined),
    }),
    [filters.status, searchParams],
  );

  if (!isLoading) {
    formInstance.setFieldsValue(initialValues);
  }

  const handleSubmit = () => {
    const formValues = removeEmptyFields(formInstance.getFieldsValue(true));
    submit(
      {
        tab: "reservations",
        ...formValues,
      },
      { method: "get" },
    );
  };

  const handleClear = () => {
    formInstance.resetFields();
    submit({ tab: "reservations" }, { method: "get" });
  };

  const activeFilters: ResponsiveFilterChip[] = [];
  if (initialValues.status) {
    activeFilters.push({
      key: "status",
      label: t("Status"),
      value: <ReservationStatusTag status={initialValues.status} />,
    });
  }

  return (
    <ResponsiveFilterPanel
      title={t("Filters")}
      activeFilters={activeFilters}
      isLoading={isLoading}
      onApply={handleSubmit}
      onClear={handleClear}
      closeDrawerOnClear
    >
      <Form form={formInstance} layout="vertical" initialValues={initialValues}>
        <ResponsiveFilterGrid>
          <Form.Item label={t("Status")} name="status">
            <Select
              placeholder={t("Select_a_status")}
              onChange={handleSubmit}
              allowClear
              disabled={isLoading}
            >
              {(
                [
                  APITypes.AmenityReservationStatus.Pending,
                  APITypes.AmenityReservationStatus.Approved,
                  APITypes.AmenityReservationStatus.Rejected,
                  APITypes.AmenityReservationStatus.Canceled,
                ] as APITypes.AmenityReservationStatus[]
              ).map((status) => (
                <Select.Option key={status} value={status}>
                  <ReservationStatusTag status={status} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </ResponsiveFilterGrid>
      </Form>
    </ResponsiveFilterPanel>
  );
};
